import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalPage } from "@/components/pages/LegalPage";
import { LEGAL_OWNER } from "@/lib/legal";

const TOC = [
  { id: "alcance", label: "Alcance de la licencia" },
  { id: "permitido", label: "Usos permitidos" },
  { id: "empresas", label: "Empresas, agencias y equipos" },
  { id: "clientes", label: "Entrega a clientes finales" },
  { id: "prohibido", label: "Usos prohibidos" },
  { id: "ia", label: "Marcas, NFT e inteligencia artificial" },
  { id: "titularidad", label: "Titularidad y exclusividad" },
  { id: "descargas", label: "Licencia perpetua y re-descargas" },
  { id: "actualizaciones", label: "Actualizaciones de producto" },
  { id: "copias", label: "Copias de seguridad" },
  { id: "incumplimiento", label: "Suspensión y revocación" },
  { id: "contacto", label: "Contacto y ley aplicable" },
];

export const Route = createFileRoute("/licencia")({
  head: () => ({
    meta: [
      { title: "Licencia de uso comercial — Yustiz Haro Resources" },
      {
        name: "description",
        content:
          "Licencia comercial perpetua, mundial, no exclusiva e intransferible de los recursos digitales de Yustiz Haro Resources: usos permitidos, prohibidos y alcance.",
      },
      { property: "og:title", content: "Licencia de uso comercial — Yustiz Haro Resources" },
      {
        property: "og:description",
        content: "Alcance comercial de los recursos digitales adquiridos.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recursos.yustizharo.com/licencia" },
    ],
    links: [{ rel: "canonical", href: "https://recursos.yustizharo.com/licencia" }],
  }),
  component: LicenciaPage,
});

function LicenciaPage() {
  return (
    <LegalPage
      title="Licencia de uso comercial"
      currentPath="/licencia"
      toc={TOC}
      intro={
        <p>
          Esta licencia regula el uso de los archivos digitales adquiridos en {LEGAL_OWNER.brand},
          titularidad de {LEGAL_OWNER.holder}, CUIT {LEGAL_OWNER.cuit}, con domicilio en{" "}
          {LEGAL_OWNER.address}. Al descargar o utilizar cualquier recurso aceptás estas
          condiciones, que se complementan con los{" "}
          <Link to="/terminos" className="text-primary underline underline-offset-4">
            Términos y condiciones
          </Link>{" "}
          y la{" "}
          <Link
            to="/politica-de-reembolsos"
            className="text-primary underline underline-offset-4"
          >
            Política de reembolsos
          </Link>
          .
        </p>
      }
    >
      <h2 id="alcance">1. Alcance de la licencia</h2>
      <p>
        La compra de cualquier recurso otorga al comprador una licencia de uso comercial
        perpetua, mundial, no exclusiva e intransferible sobre los archivos descargados. La
        licencia se otorga a la persona humana o jurídica que realizó la compra y no requiere
        atribución ni mención de la marca en las obras finales.
      </p>
      <p>
        La licencia es válida tanto para proyectos propios como para trabajos realizados para
        clientes, sin límite de cantidad de proyectos, de tiraje ni de duración temporal.
      </p>

      <h2 id="permitido">2. Usos permitidos</h2>
      <ul>
        <li>
          Publicidad, campañas de marca, contenido para redes sociales, cine, televisión,
          plataformas de streaming, videoclips, documentales, cortometrajes, presentaciones
          corporativas, eventos, cursos y contenido educativo.
        </li>
        <li>Trabajos realizados para clientes, agencias y productoras.</li>
        <li>
          Modificar, recortar, escalar, retemporizar, colorear, combinar y adaptar los recursos
          para integrarlos dentro de una obra audiovisual final.
        </li>
        <li>
          Uso en proyectos monetizados, incluidas piezas con pauta publicitaria y contenido con
          ingresos por plataformas.
        </li>
      </ul>

      <h2 id="empresas">3. Empresas, agencias y equipos</h2>
      <p>
        Cuando la compra la realiza una empresa, agencia o productora, sus empleados y
        colaboradores pueden utilizar los archivos exclusivamente en proyectos del comprador.
        Esas personas no pueden conservar los archivos para uso independiente, para otros
        clientes propios ni para proyectos ajenos al comprador. Al finalizar la relación laboral
        o contractual deben dejar de utilizarlos y eliminar sus copias.
      </p>

      <h2 id="clientes">4. Entrega a clientes finales</h2>
      <p>
        El cliente final de un trabajo puede recibir la obra audiovisual terminada que incorpore
        los recursos (por ejemplo, un comercial exportado o un proyecto entregado como pieza
        final). No puede recibir los archivos originales como biblioteca independiente,
        reutilizable o separable de esa obra.
      </p>

      <h2 id="prohibido">5. Usos prohibidos</h2>
      <ul>
        <li>
          Revender, redistribuir, sublicenciar, regalar, intercambiar, prestar o ceder los
          archivos originales, en todo o en parte.
        </li>
        <li>
          Publicarlos en bancos de recursos, marketplaces, comunidades de descarga, servicios de
          almacenamiento público, grupos de mensajería o repositorios accesibles por terceros.
        </li>
        <li>Incluirlos en otros packs, plantillas, plugins o productos de terceros.</li>
        <li>
          Ofrecer los archivos como descarga pública, gratuita o de pago, aunque sea de forma
          parcial o modificada, cuando conserven su carácter de recurso reutilizable.
        </li>
        <li>Compartir credenciales de acceso a la biblioteca privada de compras.</li>
      </ul>

      <h2 id="ia">6. Marcas, NFT e inteligencia artificial</h2>
      <p>
        Sin autorización previa y por escrito del titular, no está permitido utilizar los
        recursos como marca, logotipo o elemento distintivo registrable, acuñarlos o
        comercializarlos como NFT o activos digitales equivalentes, ni utilizarlos como material
        de entrenamiento o dataset para sistemas de inteligencia artificial o modelos
        generativos.
      </p>

      <h2 id="titularidad">7. Titularidad y exclusividad</h2>
      <p>
        La compra concede una licencia de uso y no transfiere la propiedad intelectual de los
        archivos, que permanece en cabeza del titular. Los recursos se licencian de forma no
        exclusiva: otros compradores pueden adquirir y utilizar los mismos archivos. No se
        ofrece ni se garantiza exclusividad de ningún tipo.
      </p>

      <h2 id="descargas">8. Licencia perpetua y servicio de re-descarga</h2>
      <p>
        La licencia sobre los archivos ya descargados es perpetua y no caduca. El servicio de
        re-descarga desde la biblioteca privada de la plataforma es una prestación adicional:
        depende de la disponibilidad técnica del sitio y puede modificarse o discontinuarse sin
        que ello afecte la licencia perpetua ya otorgada sobre los archivos descargados.
      </p>

      <h2 id="actualizaciones">9. Actualizaciones de producto</h2>
      <p>
        Las actualizaciones, correcciones y ampliaciones publicadas sobre un producto ya
        adquirido quedan incluidas sin costo adicional mientras el producto siga disponible en
        la plataforma. Los productos nuevos e independientes, aunque pertenezcan a la misma
        familia o categoría, no quedan incluidos automáticamente y requieren una compra
        separada.
      </p>

      <h2 id="copias">10. Copias de seguridad</h2>
      <p>
        El comprador puede realizar copias de seguridad de los archivos y almacenarlos en sus
        propios dispositivos, servidores o servicios de nube privados, siempre que esas copias
        sean de acceso restringido y se utilicen únicamente dentro del alcance de esta licencia.
        Las copias en almacenamiento compartido deben estar limitadas a las personas
        autorizadas conforme a la sección 3.
      </p>

      <h2 id="incumplimiento">11. Suspensión y revocación</h2>
      <p>
        La redistribución de los archivos, su publicación no autorizada o cualquier
        incumplimiento grave de esta licencia habilita al titular a suspender el acceso a la
        biblioteca privada y a revocar la licencia otorgada, sin perjuicio de las acciones
        legales que correspondan. La revocación se comunica al email asociado a la compra e
        indica el motivo.
      </p>

      <h2 id="contacto">12. Contacto y ley aplicable</h2>
      <p>
        Esta licencia se rige por las leyes de la {LEGAL_OWNER.law}. Para solicitar
        autorizaciones especiales o consultar sobre un uso concreto, escribí a{" "}
        <a href={`mailto:${LEGAL_OWNER.email}`}>{LEGAL_OWNER.email}</a>.
      </p>
    </LegalPage>
  );
}
